import React, { useMemo, useState } from "react";
import { Button, Title } from "react-admin";
import { Box, useMediaQuery } from "@mui/material";
import SecuredComponent from "server/security/SecuredComponent";
import permissions from "app/permissions";
import { checkPermission } from "server/security/utils";
import { getPermissions } from "auth/security/securityUtils";
import CustomFilter from "features/CustomFilter";
import { useProfessorsFilter, useRoomsFilter, useStudentFilter, useStudyGroupsFilter } from "./usePreviewFilters";
import ScheduleTable from "./components/ScheduleTable";
import { MOBILE_BREAKPOINT, ViewTypeSchedule } from "utils/constants";

const ListSchedules = () => {
  const canSeeClassroomsScheduleTab = checkPermission(getPermissions(), permissions.canSeeClassroomsScheduleTab);
  const canSeeStudentScheduleTab = checkPermission(getPermissions(), permissions.canSeeStudentScheduleTab);

  let DEFAULT_TAB = null;
  if (!canSeeClassroomsScheduleTab && !canSeeStudentScheduleTab) {
    DEFAULT_TAB = 2;
  } else {
    DEFAULT_TAB = 1;
  }
  const [selectedTab, setSelectedTab] = useState(DEFAULT_TAB);
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);

  const [roomsFilters, setRoomsFilters] = useState(null);
  const [professorsFilters, setProfessorsFilters] = useState(null);
  const [studentFilters, setStudentFilters] = useState(null);
  const [studyGroupsFilters, setStudyGroupsFilters] = useState(null);

  const roomsDraft = useRoomsFilter();
  const professorsDraft = useProfessorsFilter();
  const studentDraft = useStudentFilter();
  const studyGroupsDraft = useStudyGroupsFilter();

  // The selected tab's filter: title, draft and where its query is committed.
  const activeFilter = {
    1: { title: "Разписание за зала", draft: roomsDraft, commit: setRoomsFilters },
    2: { title: "Разписание за преподавател", draft: professorsDraft, commit: setProfessorsFilters },
    3: { title: "Разписание за студент", draft: studentDraft, commit: setStudentFilters },
    4: { title: "Разписание за студентска група", draft: studyGroupsDraft, commit: setStudyGroupsFilters },
  }[selectedTab];

  const handleTabChange = newTab => {
    setRoomsFilters(null);
    setProfessorsFilters(null);
    setStudentFilters(null);
    setStudyGroupsFilters(null);
    setSelectedTab(newTab);
  };

  const isRooms = selectedTab === 1;
  const isProfessors = selectedTab === 2;
  const isStudents = selectedTab === 3;
  const isStudyGroups = selectedTab === 4;

  const fullStudentFilters = useMemo(() => {
    if (!studentFilters) return null;
    return {
      ...studentFilters,
      tab: selectedTab,
    };
  }, [studentFilters, selectedTab]);

  const fullProfessorFilters = useMemo(() => {
    if (!professorsFilters) return null;
    return {
      ...professorsFilters,
      tab: selectedTab,
    };
  }, [professorsFilters, selectedTab]);

  const fullRoomFilters = useMemo(() => {
    if (!roomsFilters) return null;
    return {
      ...roomsFilters,
      tab: selectedTab,
    };
  }, [roomsFilters, selectedTab]);

  const fullStudyGroupsFilter = useMemo(() => {
    if (!studyGroupsFilters) return null;
    return {
      ...studyGroupsFilters,
      tab: selectedTab,
    };
  }, [studyGroupsFilters, selectedTab]);

  return (
    <>
      {!roomsFilters && !professorsFilters && !studentFilters && !studyGroupsFilters && (
        <Title title={"Преглед на седмично разписание"} />
      )}
      <Box sx={{ display: "flex", gap: 3, mt: 4, justifyContent: "center" }}>
        <SecuredComponent permission={permissions.canSeeClassroomsScheduleTab}>
          <Button
            variant={isRooms ? "contained" : "outlined"}
            label="Зала"
            sx={{ p: 1, minWidth: 150 }}
            onClick={() => handleTabChange(1)}
          />
        </SecuredComponent>
        <SecuredComponent permission={permissions.canSeeProfessorsScheduleTab}>
          <Button
            variant={isProfessors ? "contained" : "outlined"}
            label="Преподавател"
            sx={{ p: 1, minWidth: 150 }}
            onClick={() => handleTabChange(2)}
          />
        </SecuredComponent>
        <SecuredComponent permission={permissions.canSeeStudentScheduleTab}>
          <Button
            variant={isStudents ? "contained" : "outlined"}
            label="Студент"
            sx={{ p: 1, minWidth: 150 }}
            onClick={() => handleTabChange(3)}
          />
        </SecuredComponent>
        <SecuredComponent permission={permissions.canSeeStudyGroupScheduleTab}>
          <Button
            variant={isStudyGroups ? "contained" : "outlined"}
            label="Учебни групи"
            sx={{ p: 1, minWidth: 150 }}
            onClick={() => handleTabChange(4)}
          />
        </SecuredComponent>
      </Box>
      {activeFilter && (
        <CustomFilter
          title={activeFilter.title}
          cardWidth={isMobile ? "100%" : "700px"}
          filters={activeFilter.draft.fields}
          buttonLabel="Покажи"
          buttonDisabled={!activeFilter.draft.isComplete}
          buttonOnClick={() => activeFilter.commit(activeFilter.draft.query)}
        />
      )}

      {isRooms && roomsFilters && (
        <ScheduleTable
          filters={fullRoomFilters}
          title="Седмично разписание - Зала"
          viewType={ViewTypeSchedule.Room}
        />
      )}

      {isProfessors && professorsFilters && (
        <ScheduleTable
          filters={fullProfessorFilters}
          title="Седмично разписание - Преподавател"
          viewType={ViewTypeSchedule.Professor}
        />
      )}

      {isStudents && studentFilters && (
        <ScheduleTable
          filters={fullStudentFilters}
          title="Седмично разписание - Студент"
          viewType={ViewTypeSchedule.Student}
        />
      )}

      {isStudyGroups && studyGroupsFilters && (
        <ScheduleTable
          filters={fullStudyGroupsFilter}
          title="Седмично разписание - Учебна група"
          viewType={ViewTypeSchedule.StudyGroup}
        />
      )}
    </>
  );
};

export default ListSchedules;
